import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Beaker, BarChart3, Database, FileText, Upload, BookTemplate,
  Bot, Shield, Zap, Globe, Cpu, CheckCircle, Loader2, FileSpreadsheet,
  ArrowRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const QUICK_ACTIONS = [
  { icon: Database, label: 'Profile My Dataset', message: 'Profile my dataset and show a spaghetti plot', color: 'text-emerald-400 bg-emerald-500/10', needsData: true },
  { icon: Beaker, label: 'Run NCA Analysis', message: 'Run NCA analysis on this dataset', color: 'text-blue-400 bg-blue-500/10', needsData: true },
  { icon: Cpu, label: 'Build PopPK Model', message: 'Build a PopPK model with covariate analysis', color: 'text-purple-400 bg-purple-500/10', needsData: true },
  { icon: CheckCircle, label: 'Run QC Diagnostics', message: 'Run quality control diagnostics on my analysis', color: 'text-red-400 bg-red-500/10', needsData: true },
];

const NO_DATA_PROMPTS = [
  { label: 'What can Pharmax.ai do?', message: 'What can you do? Tell me about your capabilities.' },
  { label: 'FDA PopPK requirements', message: 'What are the FDA requirements for PopPK studies?' },
  { label: 'NCA vs PopPK guidance', message: 'When should I use NCA vs PopPK analysis?' },
  { label: 'Help me design a PK study', message: 'Help me design a Phase 1 PK study for a small molecule.' },
];

const TEMPLATE_SHORTCUTS = [
  { key: 'nca-single-dose', label: 'Single-Dose NCA', desc: '4 steps', emoji: '🧪' },
  { key: 'popk-standard', label: 'Standard PopPK', desc: '8 steps', emoji: '🧬' },
  { key: 'er-efficacy-binary', label: 'Efficacy E-R', desc: '4 steps', emoji: '📈' },
];

const SUGGESTED_WITH_DATA = [
  'Show concentration-time plot',
  'Run NCA with dose proportionality assessment',
  'Run covariate screening',
  'Generate a regulatory NCA report',
];

const SAMPLE_DATASETS = [
  { id: 'sample-pk-single-dose', name: 'Single-Dose PK Study', icon: '💊',
    desc: '24 subjects · 264 obs · 100mg oral', detail: '1-compartment model with first-order absorption. Great for learning NCA and basic PopPK.',
    best: 'NCA, Basic PopPK' },
  { id: 'sample-pk-multi-dose', name: 'Multi-Dose PopPK Study', icon: '📊',
    desc: '80 subjects · ~800 obs · 3 dose groups', detail: 'Rich dataset with covariates (WT, AGE, CRCL, ALT). Includes renal impairment subjects.',
    best: 'PopPK, Covariate Analysis, QC' },
  { id: 'sample-er-binary', name: 'Exposure-Response (Binary)', icon: '📈',
    desc: '300 subjects · Placebo + 3 doses', detail: 'Phase 2 efficacy data with logistic E-R model. Includes ECOG and prior therapy covariates.',
    best: 'E-R Analysis, Dose Justification' },
];

const CAPABILITIES = [
  { emoji: '📊', label: 'Data' }, { emoji: '🧪', label: 'NCA' }, { emoji: '🧬', label: 'PopPK' },
  { emoji: '💊', label: 'PKPD' }, { emoji: '📈', label: 'E-R' }, { emoji: '🫁', label: 'PBPK' },
  { emoji: '🎲', label: 'Sim' }, { emoji: '✅', label: 'QC' }, { emoji: '📜', label: 'Regulatory' },
];

export default function EmptyChatState({ dataset, projectId, onSendMessage, onUploadFile, onLaunchTemplate, onDatasetLoaded }) {
  const [showSamples, setShowSamples] = useState(false);
  const [loadingSample, setLoadingSample] = useState(null);

  const handleLoadSample = async (sampleId) => {
    setLoadingSample(sampleId);
    try {
      const res = await axios.post(`${API_BASE}/sample-datasets/${sampleId}/load?project_id=${projectId || ''}`);
      const ds = res.data;
      toast.success(`Loaded: ${ds.filename} (${ds.n_subjects} subjects, ${ds.n_observations} observations)`);
      setShowSamples(false);
      onDatasetLoaded?.(ds);
      // Auto-profile — wait for state to propagate, then send
      setTimeout(() => {
        onSendMessage?.(`Profile the loaded dataset ${ds.filename}`, ds.id);
      }, 800);
    } catch (err) {
      toast.error('Failed to load sample: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingSample(null);
    }
  };

  const handleQuickAction = (action) => {
    if (action.needsData && !dataset) {
      toast.info('Please upload or load a dataset first to run this analysis.');
      return;
    }
    onSendMessage?.(action.message);
  };

  const hasData = !!dataset;

  return (
    <div className="flex flex-col items-center justify-center h-full py-6 px-4 overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }} className="text-center max-w-xl w-full">
        
        {/* Logo */}
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Beaker className="w-7 h-7 text-primary" />
        </div>

        {hasData ? (
          /* ======== WITH DATASET ======== */
          <>
            <h2 className="text-xl sm:text-2xl font-semibold" data-testid="empty-chat-title" style={{ fontFamily: 'Space Grotesk' }}>
              Ready to analyze
            </h2>
            
            {/* Dataset card */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/30 text-sm" data-testid="dataset-status-card">
              <FileSpreadsheet className="w-4 h-4 text-primary" />
              <span className="font-mono">{dataset.filename}</span>
              <span className="text-muted-foreground">· {dataset.n_subjects} subjects · {dataset.n_observations?.toLocaleString()} obs</span>
            </div>

            {/* Quick actions for data-loaded state */}
            <div className="grid grid-cols-2 gap-2 mt-5">
              {QUICK_ACTIONS.map((action, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.04 }}>
                  <Card className="card-hover cursor-pointer h-full" data-testid={`quick-action-${i}`}
                    onClick={() => onSendMessage?.(action.message)}>
                    <CardContent className="py-3 px-3 text-center">
                      <div className={`w-9 h-9 rounded-lg ${action.color} flex items-center justify-center mx-auto mb-1.5`}>
                        <action.icon className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-medium">{action.label}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Template Shortcuts */}
            <div className="mt-5">
              <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider">Workflow Templates</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                {TEMPLATE_SHORTCUTS.map((t) => (
                  <button key={t.key} onClick={() => onLaunchTemplate?.(t.key)}
                    data-testid={`template-shortcut-${t.key}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:border-primary/30 hover:bg-primary/5 transition-all text-left text-xs active:scale-[0.97]">
                    <span>{t.emoji}</span>
                    <span className="font-medium">{t.label}</span>
                    <span className="text-muted-foreground/50">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Suggested prompts */}
            <div className="mt-4">
              <div className="flex flex-wrap gap-1.5 justify-center">
                {SUGGESTED_WITH_DATA.map((prompt, i) => (
                  <button key={i} onClick={() => onSendMessage?.(prompt)}
                    data-testid={`suggested-prompt-${i}`}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-border/50 hover:border-primary/30 hover:text-primary transition-all">
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* ======== NO DATASET — Guided First Experience ======== */
          <>
            <h2 className="text-xl sm:text-2xl font-semibold" data-testid="empty-chat-title" style={{ fontFamily: 'Space Grotesk' }}>
              Get started with your analysis
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Upload your PK/PD data or try a sample dataset to explore the platform's capabilities.
            </p>

            {/* Primary CTAs — Upload or Sample */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Button variant="outline" onClick={onUploadFile} 
                data-testid="upload-data-btn"
                className="rounded-xl h-12 px-6 text-sm gap-2 border-border hover:border-primary/30">
                <Upload className="w-5 h-5" /> Upload My Data
                <span className="text-[10px] text-muted-foreground ml-1">CSV, XPT, XLSX</span>
              </Button>
              <Button onClick={() => setShowSamples(true)} 
                data-testid="use-sample-btn"
                className="rounded-xl h-12 px-6 text-sm gap-2 bg-primary/90 hover:bg-primary">
                <Database className="w-5 h-5" /> Use Sample Dataset
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mt-6 mb-4">
              <div className="flex-1 h-px bg-border/50" />
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">or ask a question</span>
              <div className="flex-1 h-px bg-border/50" />
            </div>

            {/* No-data prompts — things you can ask without data */}
            <div className="flex flex-wrap gap-2 justify-center">
              {NO_DATA_PROMPTS.map((prompt, i) => (
                <button key={i} onClick={() => onSendMessage?.(prompt.message)}
                  data-testid={`no-data-prompt-${i}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-border/50 hover:border-primary/30 hover:text-primary hover:bg-primary/5 transition-all">
                  {prompt.label}
                </button>
              ))}
            </div>

            {/* Template Shortcuts — still visible but with a note */}
            <div className="mt-6">
              <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider">Workflow Templates <span className="text-muted-foreground/40">(requires dataset)</span></p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                {TEMPLATE_SHORTCUTS.map((t) => (
                  <button key={t.key} onClick={() => {
                    toast.info('Please load a dataset first to run this workflow template.');
                  }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border/50 hover:border-primary/20 hover:bg-primary/5 transition-all text-left text-xs opacity-70 hover:opacity-100">
                    <span>{t.emoji}</span>
                    <span className="font-medium">{t.label}</span>
                    <span className="text-muted-foreground/50">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Capabilities */}
        <div className="mt-5 flex flex-wrap gap-2 justify-center">
          {CAPABILITIES.map((cap, i) => (
            <span key={i} className="text-[10px] text-muted-foreground/50">{cap.emoji} {cap.label}</span>
          ))}
        </div>
      </motion.div>

      {/* Sample Dataset Dialog */}
      <Dialog open={showSamples} onOpenChange={setShowSamples}>
        <DialogContent className="max-w-lg" data-testid="sample-dataset-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Choose a Sample Dataset</DialogTitle>
            <DialogDescription>
              Load realistic simulated pharmacokinetic data to explore the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {SAMPLE_DATASETS.map((ds) => (
              <Card key={ds.id} className={`card-hover cursor-pointer ${loadingSample === ds.id ? 'border-primary' : ''}`}
                data-testid={`sample-dataset-${ds.id}`}
                onClick={() => !loadingSample && handleLoadSample(ds.id)}>
                <CardContent className="py-4 px-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{ds.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{ds.name}</h3>
                        {loadingSample === ds.id && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{ds.desc}</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">{ds.detail}</p>
                      <p className="text-[10px] text-primary mt-1.5">Best for: {ds.best}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
